import { useNosIdMutation, useNosIdQuery } from '@/hooks/api/base.hooks';
import { api, type UpdatePassword } from '@/portal-api.definition';
import { ActionType, useI18n, useToast } from '@nosinovacao/nosid-mfe-common';
import { queryOptions } from '@tanstack/react-query';

export const passwordRequirementsQueryOptions = queryOptions({
  queryKey: ['password-requirements'],
  queryFn: () => api.get('/api/v1/security/password-requirements'),
});

export function useGetPasswordRequirements() {
  const query = useNosIdQuery(passwordRequirementsQueryOptions);
  const raw = query.data as { RequirementsAndRegex?: Record<string, string> } | undefined;
  return { ...query, data: raw?.RequirementsAndRegex };
}

export function useValidatePassword({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: () => void;
} = {}) {
  return useNosIdMutation({
    mutationFn: (password: string) =>
      api.post('/api/v1/security/validate-password', { Password: password }),
    successLogAction: () => ({ ActionType: ActionType.Submit }),
    errorLogAction: () => ({ ActionType: ActionType.Submit }),
    onSuccess,
    onError,
    ignoreSnackbarForErrors: ['*'],
  });
}

export function useUpdatePassword({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: () => void;
} = {}) {
  const toast = useToast();
  const { t } = useI18n();
  return useNosIdMutation({
    mutationFn: (payload: UpdatePassword) => api.post('/api/v1/security/new-password', payload),
    successLogAction: () => ({ ActionType: ActionType.Submit }),
    errorLogAction: () => ({ ActionType: ActionType.Submit }),
    onSuccess: () => {
      toast.show('success', t('Common.SnackBar.PasswordUpdated'));
      onSuccess?.();
    },
    onError,
    ignoreSnackbarForErrors: ['*'],
  });
}
